/**
 * Registration-aware effect guardrail.
 *
 * The structural {@link ruleJsonSchema} is component-agnostic: it can tell
 * that a `filterPropValues` effect is *shaped* correctly, but not whether the
 * prop it targets is one the host actually passes as an inline **data array**.
 * That distinction lives on the component's {@link ComponentRegistration}
 * (`filterableProps`), so this second-pass check runs only once a component is
 * known.
 *
 * It exists because `filterPropValues` is a *runtime no-op* when its prop is
 * not an existing array (`ruleEvaluator.ts → filterPropValues()` bails with a
 * `console.warn`). The classic offender is an empty-by-default allow-list
 * constraint like `RecoveryCard.allowedNatures`: by Zod shape it looks like a
 * `string[]`, but it is `undefined` until a `setProp` rule sets it, so a
 * `filterPropValues` against it changes nothing. Catching this at **authoring
 * time** turns a silent runtime no-op into an actionable error that points the
 * admin at the correct `setProp` effect.
 *
 * This module is intentionally pure — no React, no Zod, no fetch — so both
 * authoring chokepoints (the raw-JSON editor and the NL translate preview) can
 * call it after a successful structural parse.
 */

import type { ComponentRegistration, RuleEffect } from '../types';

/**
 * A single guardrail violation: the index of the offending effect within the
 * rule's `effects` array and a human-readable message ready to render inline.
 */
export interface EffectValidationError {
  /** Position of the offending effect in the `effects` array (0-based). */
  effectIndex: number;
  /** Human-readable, actionable message (suggests `setProp`). */
  message: string;
}

/**
 * Validate a rule's effects against a component registration.
 *
 * Only `filterPropValues` effects are ever flagged: an effect is rejected when
 * its `prop` is not listed in `registration.filterableProps` (the allow-list of
 * props the host passes as a real inline data array). Every other effect type
 * — including `setProp` — is left untouched.
 *
 * @param effects - The rule's ordered effect list (already structurally valid).
 * @param registration - The target component's registration; only `id` and
 *   `filterableProps` are read.
 * @returns One {@link EffectValidationError} per offending effect, in array
 *   order. Empty when every effect is allowed.
 */
export function validateEffectsAgainstRegistration(
  effects: readonly RuleEffect[],
  registration: Pick<ComponentRegistration, 'id' | 'filterableProps'>,
): EffectValidationError[] {
  const filterable = new Set(registration.filterableProps ?? []);
  const errors: EffectValidationError[] = [];

  effects.forEach((effect, effectIndex) => {
    if (effect.type !== 'filterPropValues') return;
    if (filterable.has(effect.prop)) return;

    errors.push({
      effectIndex,
      message:
        `filterPropValues cannot target "${effect.prop}" on ${registration.id} — ` +
        'this prop is not a filterable data array. Use a setProp effect to ' +
        `assign its value instead (e.g. { "type": "setProp", "prop": "${effect.prop}", "value": [...] }).`,
    });
  });

  return errors;
}
